import { Theme } from '@mui/material';
import { addPercentageToNumberText } from 'components/ChartContainer/components/Chart/graphs/helpers/gaugeChart';
import { TooltipComponentFormatterCallbackParams } from 'echarts';
import { formatDisplay } from 'helpers/formatHelper';
import { getFormat } from 'routes/BudgetModule/helpers/format';

export type CadrageEchartProps = {
    axisValue: string;
    color: string;
    marker: string;
    seriesName: string;
    data?: {
        name: string;
        label: string;
        value?: number;
    };
};

const customizeLabel = (value: CadrageEchartProps) =>
    `${value.marker}${value.seriesName} : <b>${getFormat(
        value.data?.value || '-',
    )}</b><br/>`;

export const customizePaneTooltip = ({
    values,
}: {
    values: CadrageEchartProps[];
}) => `
            ${values[0].axisValue}<br/>
            ${values.map((value) => customizeLabel(value)).join('')}
        `;

/**
 * Customize the tooltip for the cadrage graph.
 * @param params from echarts
 * @param value the amount of the metric
 * @param metric the metric name
 */
export const customizeCadrageTooltip = ({
    params,
    value,
    metric,
}: {
    params: TooltipComponentFormatterCallbackParams;
    value: number;
    metric: string;
}): string | null => {
    if (Array.isArray(params)) return null;
    return `${params.marker} ${metric} : <b>${getFormat(value || '-')}</b>`;
};

export const customizePieTooltip = ({
    value,
}: {
    value: CadrageEchartProps;
}) => `
                    ${value.marker} ${value.data?.name} : <b>${
                        value.data?.value || '0'
                    }</b>
`;

/**
 * Generates the ECharts option configuration for the cadrage gauge chart.
 *
 * @param params - The parameters for the ECharts option.
 * @param height - The height of the chart.
 * @param width - The width of the chart.
 * @param cadrageValue - The cadrage value for the gauge max.
 * @param value - The current value to be displayed on the gauge.
 * @param metric - The metric to be displayed.
 * @param theme - The theme object containing palette information.
 * @param reverseColor - A flag to reverse the color logic.
 * @returns The ECharts option configuration object.
 */
export const getCadrageEchartsOption = ({
    height,
    width,
    cadrageValue,
    value,
    metric,
    theme,
    reverseColor,
}: {
    height: number;
    width: number;
    cadrageValue: number;
    value: number;
    metric: string;
    theme: Theme;
    reverseColor: boolean;
}) => {
    const scale = Math.min(height, width);
    const valueLenght = (Math.round(value * 100) / 100).toString().length;
    const numberFontSize = scale / (valueLenght + 4);
    const numberTextFontSize = scale / 18;
    const tickFontSize = scale / 18;

    // We are not allowed to have both boundary being 0 in the gauge
    // We make a dummy value to avoid this and still display 0.
    const isInvalidBoundary = cadrageValue <= 0;

    const isSmallGraph = scale > 300;
    // we adjust some params for small screens
    const gaugeWidth = isSmallGraph ? scale / 14 : scale / 10;

    const filledRatioRaw = isInvalidBoundary
        ? Number.NaN
        : value / cadrageValue;
    const progressColor =
        value > cadrageValue === reverseColor
            ? theme.palette.error.main
            : theme.palette.success.main;

    const textColor = '#333333';
    const greyGradient = {
        type: 'linear',
        x: 0,
        y: 0,
        x2: 1,
        y2: 0,
        colorStops: [
            {
                offset: 0,
                color: '#ccc',
            },
            {
                offset: 1,
                color: '#bbb',
            },
        ],
        globalCoord: false,
    };

    const option = {
        tooltip: {
            confine: true,
            formatter: (params: TooltipComponentFormatterCallbackParams) =>
                customizeCadrageTooltip({ params, value, metric }),
        },
        textStyle: { fontFamily: 'Poppins' },
        series: [
            {
                color: progressColor,
                type: 'gauge',
                min: 0,
                max: isInvalidBoundary ? 1 : cadrageValue,
                splitNumber: 1,
                radius: '80%',
                progress: {
                    show: true,
                    width: gaugeWidth,
                },
                axisLine: {
                    lineStyle: {
                        width: gaugeWidth,
                        color: [[1, greyGradient]],
                    },
                },
                axisTick: { show: false },
                pointer: { show: false },
                axisLabel: {
                    color: textColor,
                    fontSize: tickFontSize,
                    formatter(d: number) {
                        const axisLabelNumber = isInvalidBoundary ? 0 : d;
                        return formatDisplay(
                            axisLabelNumber,
                            'money',
                            2,
                            'default',
                        );
                    },
                },
                splitLine: {
                    // Useful to have margin on the ticks
                    length: '-45%',
                    lineStyle: {
                        width: 0,
                    },
                },
                title: {
                    offsetCenter: [0, '25%'],
                    fontSize: numberTextFontSize,
                    lineHeight: scale / 15,
                    rich: {}, // needed for lineHeight to work
                },
                detail: {
                    color: textColor,
                    fontSize: numberFontSize,
                    offsetCenter: [0, '-10%'],
                    formatter(d: number) {
                        return formatDisplay(d, 'money', 2, 'default');
                    },
                },
                data: [
                    {
                        value,
                        name: addPercentageToNumberText(
                            filledRatioRaw,
                            'money',
                            0,
                            cadrageValue ?? 0,
                        ),
                    },
                ],
            },
        ],
    };
    return option;
};
